import { useState } from "react";
import { toast } from "sonner";
import { Check, Download, Phone } from "lucide-react";
import { API, api, errMsg, FACT_TYPES } from "@/lib/api";

const MAX_Q = 5;

const typeFor = (cat) => (cat === "tools used" ? "tool" : cat === "biggest win" ? "win" : "number");

function QuestionRow({ q, client, answered, onSaved }) {
  const [text, setText] = useState("");
  const [type, setType] = useState(typeFor(q.category));
  const save = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/clients/${client.id}/facts`, { role_id: q.role_id, text: text.trim(), type, source: "call", verified: true, question_id: q.id });
      setText(""); toast.success("Saved to FactSheet"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="py-2.5 border-t border-border first:border-0">
      <div className="flex items-start gap-2 text-sm">
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 shrink-0 mt-0.5">{q.category}</span>
        <span className="text-slate-200">{q.question}</span>
      </div>
      {answered.map((f) => (
        <div key={f.id} className="ml-2 mt-1 text-xs text-emerald-300 flex items-center gap-1"><Check size={11} /> {f.text} <span className="text-slate-500">({f.type})</span></div>
      ))}
      <div className="flex gap-2 mt-2">
        <input data-testid={`metrics-call-question-input-${q.id}`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="Type the client's answer, e.g. 'Team of 6 analysts'"
          className="flex-1 bg-slate-900 border border-border rounded-md px-3 py-1.5 text-sm outline-none focus:border-blue-500" />
        <select value={type} onChange={(e) => setType(e.target.value)} className="bg-slate-900 border border-border rounded-md px-2 text-xs" data-testid={`metrics-call-type-${q.id}`}>
          {FACT_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
        <button data-testid={`metrics-call-save-${q.id}`} onClick={save} className="px-3 rounded-md bg-blue-600 hover:bg-blue-500 text-xs">Save</button>
      </div>
    </div>
  );
}

export default function MetricsCall({ client, facts, onChange }) {
  const roles = client.parsed_resume?.roles || [];
  const qs = client.call_questions || [];
  if (!qs.length) return <div className="card-surface text-sm text-muted-foreground">Parse the resume first to generate the call question list.</div>;
  return (
    <div className="space-y-4" data-testid="metrics-call">
      <div className="card-surface flex items-center gap-4" data-testid="questionnaire-card">
        <Phone size={18} className="text-blue-300 shrink-0" />
        <div className="flex-1 text-sm">
          <div className="font-semibold">Send these questions to the client</div>
          <div className="text-muted-foreground">Download them as one Word file, send it to the client, then type their answers below. Every question is optional: skip what the client doesn't know, and the AI writes those points from the data it has, without making up numbers.</div>
        </div>
        <a data-testid="questionnaire-download" href={`${API}/clients/${client.id}/questionnaire`}
          className="shrink-0 inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white"><Download size={14} />Download questions (Word)</a>
      </div>
      {roles.map((r) => {
        const verified = facts.filter((f) => f.role_id === r.id && f.type === "number" && f.verified).length;
        return (
          <div key={r.id} className="card-surface" data-testid={`metrics-role-${r.id}`}>
            <div className="flex items-center mb-2">
              <div><div className="font-semibold">{r.title}</div><div className="text-xs text-muted-foreground">{r.company} · {r.start} – {r.end}</div></div>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full border border-border text-slate-400" data-testid={`metrics-count-${r.id}`}>
                {verified} verified numbers · optional
              </span>
            </div>
            {qs.filter((q) => q.role_id === r.id).slice(0, MAX_Q).map((q) => (
              <QuestionRow key={q.id} q={q} client={client} answered={facts.filter((f) => f.question_id === q.id)} onSaved={onChange} />
            ))}
          </div>
        );
      })}
    </div>
  );
}
